import { useState, useEffect } from 'react';
import {
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@mui/material';
import { useAppDispatch } from '../../../../../store';
import { addTemplate, updateTemplate } from '../../../store/slices/whatsappTemplateSlice';
import { useLeadStages } from '../../../hooks/useLeadStages';
import type { WhatsAppTemplate } from '../../../types/whatsappTemplate';

interface CreateWhatsAppTemplateModalProps {
  isOpen: boolean;
  onRequestClose: () => void;
  editingTemplate?: WhatsAppTemplate | null;
}

const CreateWhatsAppTemplateModal: React.FC<CreateWhatsAppTemplateModalProps> = ({
  isOpen,
  onRequestClose,
  editingTemplate = null,
}) => {
  const dispatch = useAppDispatch();
  const { stepperStages } = useLeadStages();

  const [formData, setFormData] = useState({
    stage: '' as string,
    title: '',
    message: '',
  });

  // Reset form when modal opens/closes or editingTemplate changes
  useEffect(() => {
    if (isOpen) {
      if (editingTemplate) {
        setFormData({
          stage: editingTemplate.stage,
          title: editingTemplate.title,
          message: editingTemplate.message,
        });
      } else {
        setFormData({
          stage: '',
          title: '',
          message: '',
        });
      }
    }
  }, [isOpen, editingTemplate]);

  const handleSave = () => {
    if (!formData.stage || !formData.title || !formData.message.trim()) {
      alert('Please fill in all fields');
      return;
    }

    if (editingTemplate) {
      dispatch(
        updateTemplate({
          ...editingTemplate,
          stage: formData.stage as string,
          title: formData.title,
          message: formData.message,
        })
      );
    } else {
      dispatch(
        addTemplate({
          stage: formData.stage as string,
          title: formData.title,
          message: formData.message,
        })
      );
    }

    handleClose();
  };

  const handleClose = () => {
    setFormData({
      stage: '',
      title: '',
      message: '',
    });
    onRequestClose();
  };

  if (!isOpen) return null;

  return (
    <div
      className={`fixed inset-0 z-50 transition-opacity duration-300 ${isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black bg-opacity-50"
        onClick={handleClose}
      ></div>

      {/* Slide-in Panel */}
      <div
        className={`absolute right-0 top-0 h-full w-[30%] bg-white shadow-xl transform transition-transform duration-1000 ease-in-out ${isOpen ? 'translate-x-0' : 'translate-x-full'
          }`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200">
            <h2 className="text-xl font-semibold text-gray-900">
              {editingTemplate ? 'Edit Template' : 'Create New Template'}
            </h2>
            <button
              onClick={handleClose}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors duration-200"
              type="button"
            >
              ✕
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-6">
            <form
              onSubmit={(e) => {
                e.preventDefault();
                handleSave();
              }}
            >
              <div className="mb-4">
                <FormControl fullWidth>
                  <InputLabel>Stage</InputLabel>
                  <Select
                    value={formData.stage}
                    onChange={(e) =>
                      setFormData({ ...formData, stage: e.target.value as string })
                    }
                    label="Stage"
                  >
                    {stepperStages.map((stage) => (
                      <MenuItem key={stage.key} value={stage.key}>
                        {stage.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </div>

              <div className="mb-4">
                <TextField
                  label="Template Title"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  fullWidth
                  placeholder="e.g., Welcome Message, Follow-up Reminder"
                />
              </div>

              <div className="mb-6">
                <TextField
                  label="Message"
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  fullWidth
                  multiline
                  rows={8}
                  placeholder="Enter your WhatsApp message template here..."
                  helperText="This message will be pre-filled when users select this template"
                />
              </div>

              {/* Action Buttons */}
              <div className="flex flex-wrap justify-between gap-3 mt-6">
                <button
                  type="button"
                  onClick={handleClose}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={!formData.stage || !formData.title || !formData.message.trim()}
                  className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {editingTemplate ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateWhatsAppTemplateModal;
